import { NavLink } from 'react-router';
import { Users, Network, FileText, ShieldCheck, Laptop, GitBranch, MonitorSmartphone, LayoutGrid, ScrollText, History, Stethoscope, PackageOpen, PackageCheck, Boxes, Clock, BarChart3, Gavel } from 'lucide-react';
import { ADMIN_ROLES } from '@staff-app/shared-types';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import { useDunningReviewAccess } from '@/features/dunning-review/hooks/useDunningReview';

interface SidebarLinkProps {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  testId?: string;
  /**
   * Match this path EXACTLY rather than also matching its descendants.
   *
   * NavLink is active for child routes by default, which is what makes `/staff/customers` stay
   * lit while you are inside a customer. Set this only where another NAV ITEM lives under the
   * path — otherwise both entries light up and the sidebar shows two current pages.
   */
  end?: boolean;
}

function SidebarLink({ to, icon: Icon, children, testId, end }: SidebarLinkProps) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          'cursor-pointer',
          isActive
            ? 'bg-sidebar-active text-foreground'
            : 'text-muted-foreground hover:bg-panel-hover',
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{children}</span>
    </NavLink>
  );
}

export function StaffSidebar() {
  const adminUser = useAuthStore((s) => s.adminUser);
  const roles = adminUser?.roles ?? [];
  const hasRole = (r: string) => roles.includes(r as (typeof roles)[0]);
  const isRoot = hasRole(ADMIN_ROLES.ROOT);
  const showCustomers = isRoot || hasRole(ADMIN_ROLES.SUPPORT);
  const showNetwork = isRoot || hasRole(ADMIN_ROLES.ENGINEER) || hasRole(ADMIN_ROLES.SUPPORT);
  const showMyDevices = isRoot || hasRole(ADMIN_ROLES.SUPPORT);
  // Trust Center — role ≥ support (mirrors the route guard).
  const showTrust = isRoot || hasRole(ADMIN_ROLES.ENGINEER) || hasRole(ADMIN_ROLES.SUPPORT);
  // Troubleshooting — root, engineer, support (mirrors the route guard).
  const showTroubleshoot = isRoot || hasRole(ADMIN_ROLES.ENGINEER) || hasRole(ADMIN_ROLES.SUPPORT);
  // Devices Fulfillment — root + fulfillment ONLY, narrower than everything around it. Issuing a
  // demo coupon mints spendable money with no invoice and no approval, so the role IS the control
  // (mirrors the route guard AND DemoCouponsResolver's @RequireRoles).
  const showFulfillment = isRoot || hasRole(ADMIN_ROLES.FULFILLMENT);

  // Dunning Review — BOTH gates, and both are load-bearing.
  //
  // The role half mirrors the route guard (root/billing) like every other
  // entry here. Without it a READONLY user — which is what every @optisigns.com
  // address is auto-assigned on FIRST sign-in — sees a link that AdminRoleRoute
  // then refuses, so the sidebar advertises a page it cannot open.
  //
  // The second half is the server's `canDecideDunningReview` — ROOT or BILLING since
  // 2026-09-23 (it replaced the manager email allowlist). Read off `staffMe` rather
  // than recomputed here so the rule lives in ONE place; it reads false while
  // `staffMe` is loading and on error, so the entry never flashes in for the wrong person.
  const { canDecide: isDunningManager } = useDunningReviewAccess();
  const showDunningReview = (isRoot || hasRole(ADMIN_ROLES.BILLING)) && isDunningManager;

  return (
    <aside
      className="flex h-full w-60 shrink-0 flex-col border-r bg-muted"
      data-testid="staff-sidebar"
    >
      <div className="flex h-14 shrink-0 items-center border-b px-4">
        <span className="text-sm font-semibold text-foreground">{'OptiSigns Staff'}</span>
      </div>

      {/* min-h-0 is what makes this scroll: in a flex column a child's default min-height is its
          CONTENT, so without it the nav simply grows past the aside and the shell's
          overflow-hidden clips the last links (Connection Audit was unreachable). */}
      <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-3">
        {showCustomers ? (
          <SidebarLink to="/staff/customers" icon={Users} testId="nav-customers">
            {'Customers'}
          </SidebarLink>
        ) : null}
        {/* Billing reaches OptiHub for the Billing Console's Dunning control only. */}
        {showCustomers || hasRole(ADMIN_ROLES.BILLING) ? (
          <SidebarLink to="/staff/optihub" icon={LayoutGrid} testId="nav-optihub">
            {'OptiHub'}
          </SidebarLink>
        ) : null}

        {showTrust ? (
          <SidebarLink to="/staff/trust-center" icon={ShieldCheck} testId="nav-trust-center">
            {'Trust Center'}
          </SidebarLink>
        ) : null}

        {showTroubleshoot ? (
          <SidebarLink to="/staff/troubleshoot" icon={Stethoscope} testId="nav-troubleshoot">
            {'Troubleshooting'}
          </SidebarLink>
        ) : null}

        {/* ONE entry for the whole device-order journey. Coupon issuing, the Shopify order it
            becomes, and the international orders the store cannot serve are three sections
            inside it (DeviceOrdersShell), not three nav items — they are the same job at
            different stages, and splitting them crowded the sidebar.
            testId stays `nav-order-devices`: the access e2e specs assert on it.

            GATED ON `showTroubleshoot` (root/engineer/support) AND NOT ON THE UNION OF THE THREE
            TABS. It briefly was the union, so that a `fulfillment` user could reach International
            Requests — and that shipped a nav entry leading to a page which refuses them, because
            this link points at the bare path whose INDEX is the coupon queue. `demo-coupons-
            access.spec.ts` caught it on qa. International Requests is now root-only (requester,
            2026-09-15), so root reaches it through this entry and the union has nothing left to
            express. If a non-root role is ever given that tab again, this entry needs a
            DESTINATION that role can use — widening the gate alone reproduces the same bug. */}
        {showTroubleshoot ? (
          <SidebarLink to="/staff/order-devices" icon={PackageOpen} testId="nav-order-devices">
            {'Devices Order'}
          </SidebarLink>
        ) : null}

        {/* Demo-device coupons: hardware GIVEN AWAY, with no invoice, no payment and no chase.
            It began as a third tab inside Devices Order and moved out here (requester,
            2026-08-24) — it produces the same kind of Shopify code, but it shares none of the
            invoice machinery those two sections are built around, so it reads better as its own
            destination than as an exception wedged into that flow.
            Directly under Devices Order, but on a NARROWER gate than it: root + fulfillment. */}
        {showFulfillment ? (
          <SidebarLink
            to="/staff/devices-fulfillment"
            icon={PackageCheck}
            testId="nav-devices-fulfillment"
          >
            {'Devices Fulfillment'}
          </SidebarLink>
        ) : null}

        {/* Directly above Document Center (requester, 2026-09-23). */}
        {showDunningReview ? (
          <SidebarLink to="/staff/dunning-review" icon={Gavel} testId="nav-dunning-review">
            {'Dunning Review'}
          </SidebarLink>
        ) : null}

        {/* Gated on showCustomers (root + support), NOT showTroubleshoot — the /documents
            API excludes engineer, so the wider gate would show a link that 403s. */}
        {showCustomers ? (
          <SidebarLink to="/staff/document-center" icon={FileText} testId="nav-document-center">
            {'Document Center'}
          </SidebarLink>
        ) : null}

        {showNetwork ? (
          <>
            <div className="mt-4 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {'Network'}
            </div>
            <SidebarLink to="/staff/network/peers" icon={Network} testId="nav-peers">
              {'Peers'}
            </SidebarLink>
            <SidebarLink to="/staff/network/subnets" icon={Network} testId="nav-subnets">
              {'Subnets'}
            </SidebarLink>
          </>
        ) : null}

        <div className="mt-4 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {'My Account'}
        </div>
        <SidebarLink to="/staff/onboarding" icon={GitBranch} testId="nav-onboarding">
          {'Onboarding'}
        </SidebarLink>
        <SidebarLink to="/staff/enroll" icon={Laptop} testId="nav-enroll">
          {'Enroll my laptop'}
        </SidebarLink>
        {showMyDevices ? (
          <SidebarLink to="/staff/my-devices" icon={MonitorSmartphone} testId="nav-agent-devices">
            {'My Devices'}
          </SidebarLink>
        ) : null}
        {/* OptiClock. Same gate as My Devices (root + support) — the sidebar names the JOB
            ("Time Tracker"), not the internal project name. Root's Time Dashboard rollup lives
            under Administration. */}
        {showMyDevices ? (
          <SidebarLink to="/staff/opticlock" icon={Clock} testId="nav-opticlock" end>
            {'Time Tracker'}
          </SidebarLink>
        ) : null}

        {isRoot ? (
          <>
            <div className="mt-4 px-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {'Administration'}
            </div>
            <SidebarLink to="/staff/admin-users" icon={ShieldCheck} testId="nav-admin-users">
              {'Admin Users'}
            </SidebarLink>
            <SidebarLink to="/staff/optisigns-products" icon={Boxes} testId="nav-hardware-products">
              {'Products List'}
            </SidebarLink>
            <SidebarLink to="/staff/activity-log" icon={ScrollText} testId="nav-activity-log">
              {'Activity Log'}
            </SidebarLink>
            <SidebarLink to="/staff/connection-audit" icon={History} testId="nav-connection-audit">
              {'Connection Audit'}
            </SidebarLink>
            <SidebarLink
              to="/staff/opticlock/dashboard"
              icon={BarChart3}
              testId="nav-opticlock-dashboard"
            >
              {'Time Dashboard'}
            </SidebarLink>
          </>
        ) : null}
      </nav>
    </aside>
  );
}
